import { FC, ReactElement, useRef, useState, useCallback, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import Image from '../../components/atoms/images/image'
import { ACCESS_GUARANTEE_PRODUCT_TYPE, ASSETS } from '../../constants'
import { useGetMenuItems, useI18n } from '../../hooks'
import { handleAuth, clearOAuthState } from '../../services'
import { useAppSelector } from '../../store'
import { ProductWithVariants } from '../../types'
import { getAmountWithCurrency, getOfferings, getOfferingsByName, isIE } from '../../utils'
import Icon from '../atoms/images/icon'
import CartDrawer from '../cart/cartDrawer'
import Drawer from '../drawer'
import DrawerNavMenu from '../menu/drawerNavMenu'
import Navbar from '../menu/navbar'
import { MenuItem } from '../menu/navbar/navBarItems'
import SearchBar from '../searchBar'
import SearchBarResultModel from '../searchBar/searchBarResultModel'
import Tooltip from '../tooltip/tooltip'
import UpgradeBrowser from '../upgradeBrowser'

import './index.css'

const Header: FC = (): ReactElement => {
  const { products } = useAppSelector((state) => state.productReducer)
  const { cartItems } = useAppSelector((state) => state.cartReducer)
  const { settings, isBootstrapLoaded } = useAppSelector((state) => state.appConfigReducer)
  const { user } = useAppSelector((state) => state.userReducer)

  const logoFile = 'ontheHub-logo-main.svg'
  const logoSrc = `${ASSETS}/kivuto/logo/${logoFile}`

  const menuRef = useRef<HTMLDivElement>(null)
  const navMenuRef = useRef<HTMLDivElement>(null)
  const cartDrawerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const history = useHistory()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [toggleSearchInput, setToggleSearchInput] = useState(false)
  const [toggleCartDrawer, setToggleCartDrawer] = useState(false)
  const [toggleNavMenu, setToggleNavMenu] = useState(false)
  const [toggleSearchResult, setToggleSearchResult] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [filteredProductsArray, setFilteredProductsArray] = useState<ProductWithVariants[] | undefined>(undefined)
  const [offerings, setOfferings] = useState<ProductWithVariants[]>([])

  const [rightMenuItems, leftMenuItems] = useGetMenuItems({ navTitles: true }) as [MenuItem[], MenuItem]
  const userNameContainerRef = useRef<HTMLDivElement>(null)
  const [userNameContainerHeight, setUserNameContainerHeight] = useState(0)

  const handleSearchClick = () => {
    setToggleSearchInput(!toggleSearchInput)
    searchQuery.length !== 0 && setSearchQuery('')
    toggleSearchResult && setToggleSearchResult(!toggleSearchResult)
  }
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node) && !buttonRef.current?.contains(event.target as Node)) {
        setToggleSearchInput(false)
      }

      if (!cartDrawerRef.current?.contains(event.target as Node)) {
        setToggleCartDrawer(false)
      }

      if (!navMenuRef.current?.contains(event.target as Node)) {
        setToggleNavMenu(false)
      }
    }
    const closeSearchHandler = (event: KeyboardEvent) => event.key === 'Escape' && setToggleSearchInput(false)
    document.addEventListener('mousedown', handler)
    document.addEventListener('keyup', closeSearchHandler)

    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keyup', closeSearchHandler)
    }
  }, [])

  useEffect(() => {
    if (products) setOfferings(getOfferings(products))
  }, [products])

  const onSearchInputFilterProduct = useCallback(
    (searchQuery: string) => {
      const filteredProducts: ProductWithVariants[] = getOfferingsByName(offerings, searchQuery)
      setFilteredProductsArray(filteredProducts)
    },
    [offerings]
  )
  const { t } = useI18n()
  const getTotalSavings = useCallback(() => {
    const totalSavings = cartItems.reduce((prev, curr) => {
      if (curr.variant) {
        const { pricing } = curr.variant
        const currentSavings = ((pricing.msrp ? pricing.msrp : pricing.price) - pricing.price) * curr.quantity
        return prev + currentSavings
      } else {
        return prev
      }
    }, 0)
    return `${t('Common_save').toUpperCase()} ${getAmountWithCurrency(
      settings.locale.language,
      settings.currency.currencyAbbreviation,
      totalSavings
    )}`
  }, [cartItems, settings.currency.currencyAbbreviation, settings.locale.language, t])

  const Badge: FC<{ amount: number }> = ({ amount }) => {
    return (
      <div className="cart-icon-button-badge">
        <span className="cart-badge-content">{amount}</span>
      </div>
    )
  }

  const clearAndAuth = async (): Promise<void> => {
    await clearOAuthState()
    await handleAuth()
  }

  const openCartDrawer = useCallback(() => {
    setToggleCartDrawer(true)
  }, [])

  const closeCartDrawer = useCallback(() => {
    setToggleCartDrawer(false)
  }, [])

  const openNavMenuDrawer = useCallback(() => setToggleNavMenu(true), [])
  const closeNavMenuDrawer = useCallback(() => setToggleNavMenu(false), [])

  const debouncedSearchResult = useDebouncedCallback((e) => {
    onSearchInputFilterProduct(e.target.value)
    !toggleSearchResult && setToggleSearchResult(!toggleSearchResult)
    e.target.value === '' && setToggleSearchResult(!toggleSearchResult)
  }, 500)

  const UpgradeBrowserBanner = (props: { warn: boolean }) => {
    if (!props.warn) return <></>

    return (
      <div className="upgradeBrowserBanner">
        <UpgradeBrowser />
      </div>
    )
  }

  return (
    <>
      <div className="header-container-back"></div>
      <div className="header-container">
        <UpgradeBrowserBanner warn={isIE()} />
        <div className="header-menu">
          <div className="header-right-elements" role="banner">
            <button className="drawer-menu-button" aria-label="Menu Drawer" onClick={openNavMenuDrawer}>
              <i className="feather-menu"></i>
            </button>
            <Link to="/" id="onthehub-main-Logo" aria-label="Redirection to main page">
              <Image className="logo" src={logoSrc} alt={'ontheHub-logo'} size={{ width: 140, height: 36 }} />
            </Link>
            <Navbar menuItems={rightMenuItems} />
          </div>
          <div
            className="right-elements"
            role="navigation"
            aria-label={`${t('Common_navigationFor')}: ${t('Common_search')}, ${user ? t('Common_userManagement') : t('Common_Login')}, ${t(
              'Common_cart'
            )}`}
          >
            <button ref={buttonRef} className="search-box btn-search" onClick={handleSearchClick} aria-label="toggle-search" tabIndex={0}>
              <i className="feather-search" />
            </button>
            <div className="signin-menu">
              <i className="feather-user" />
              {user ? (
                <div
                  className="navbar-username-container"
                  ref={userNameContainerRef}
                  role="link"
                  tabIndex={0}
                  onMouseEnter={() => {
                    if (userNameContainerRef.current) {
                      setUserNameContainerHeight(userNameContainerRef.current.offsetHeight)
                    }
                  }}
                >
                  <Link to={user.profile.isGuestUser ? '/account/order-history' : '/account'}>
                    <span className="user-name">Hi, {user.profile.firstName || user.username}</span>
                  </Link>
                  <div className="nav-item-boarder"></div>
                  {leftMenuItems.dropdownMenu && (
                    <div className="nav-dropdown-menu" style={{ top: `${userNameContainerHeight}px` }}>
                      {leftMenuItems.dropdownMenu.map((item, i) => {
                        return item.hiddenWhenUserIsGuest && user.profile.isGuestUser ? null : item.external ? (
                          <a key={`dropdown-menu-${i}`} href={item.url}>
                            {item.title}
                          </a>
                        ) : (
                          <Link key={`dropdown-menu-${i}`} to={item.url}>
                            {item.title}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              ) : (
                isBootstrapLoaded && (
                  <span
                    role="button"
                    aria-label="sign in"
                    tabIndex={0}
                    onClick={async () => await clearAndAuth()}
                    onKeyPress={async (e) => e.key === 'Enter' && (await clearAndAuth())}
                  >
                    {t('Common_SignIn')}
                  </span>
                )
              )}
            </div>
            <Tooltip text={cartItems.length ? getTotalSavings() : t('Common_emptyCart')}>
              <button
                aria-label={`${t('Cart_OpenCartDrawer')}, ${cartItems.length ? getTotalSavings() : t('Common_emptyCart')},  ${
                  cartItems.length ? t(`Cart_Item${cartItems.length > 1 ? 's' : ''}InCart`, { total: cartItems.length }) : ''
                }.`}
                className="cart-btn-wrapper btn-wrapper"
                onClick={openCartDrawer}
                onKeyPress={(e) => e.key && e.key === 'Enter' && openCartDrawer()}
              >
                <div className="btn-wrapper">
                  <i className="feather-shopping-cart" />
                  {!!cartItems.length && (
                    <Badge
                      amount={
                        [...cartItems].filter((ci) => !ci.cartItemApiInfo.productVariantType.endsWith(ACCESS_GUARANTEE_PRODUCT_TYPE)).length
                      }
                    />
                  )}
                </div>
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
      <div ref={menuRef}>
        {toggleSearchInput ? (
          <>
            <SearchBar
              inputRef={searchInputRef}
              searchQuery={searchQuery}
              toggleSearchbar={setToggleSearchInput}
              onInputChange={(e) => {
                setSearchQuery(e.target.value)
                debouncedSearchResult(e)
              }}
            />
            <div>
              {toggleSearchResult && filteredProductsArray && filteredProductsArray.length > 0 ? (
                <div className="search-result-model-container fade-in">
                  <span role="alert" className="for-screen-reader">
                    Found {filteredProductsArray.length} items for {searchQuery}
                  </span>
                  <SearchBarResultModel
                    searchQuery={searchQuery}
                    products={filteredProductsArray}
                    togglePopUp={() => setToggleSearchInput(false)}
                  />
                  {filteredProductsArray.length > 4 ? (
                    <Link
                      to={`/shop?search=${encodeURIComponent(searchQuery)}`}
                      className="all-result-link"
                      onClick={() => setToggleSearchInput(false)}
                    >
                      {`${t('Common_showMoreResults')} (${filteredProductsArray.length})`}
                    </Link>
                  ) : null}
                </div>
              ) : filteredProductsArray && !filteredProductsArray.length ? (
                <div className="search-no-result-container">
                  <Icon icon="frown.svg" className="sad-icon" alt="sad image" size={{ width: 15, height: 15 }} />
                  <span role="alert">
                    {t('Common_searchNotFound')} {`"${searchQuery}"`}
                  </span>
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>

      <div ref={cartDrawerRef}>
        {toggleCartDrawer ? (
          <>
            <Drawer t={t} onClose={closeCartDrawer} anchor="right">
              <CartDrawer t={t} settings={settings} closeDrawer={closeCartDrawer} />
            </Drawer>
          </>
        ) : null}
      </div>

      <div ref={navMenuRef}>
        {toggleNavMenu ? (
          <>
            <Drawer t={t} onClose={closeNavMenuDrawer} anchor="left">
              <DrawerNavMenu t={t} menuItems={rightMenuItems} user={user} history={history} closeDrawer={closeNavMenuDrawer} />
            </Drawer>
          </>
        ) : null}
      </div>
    </>
  )
}

export default Header
